'use client';

import React from 'react';
import { Shield } from 'lucide-react';
import PolicyLayout, {
  PolicySection,
  PolicySubSection,
  PolicyList,
  PolicyCallout,
  TocSection,
} from '@/components/PolicyLayout';

const sections: TocSection[] = [
  { id: 'overview', title: 'Overview' },
  { id: 'data-collected', title: 'Data We Collect' },
  { id: 'resume-uploads', title: 'Resume Uploads' },
  { id: 'ai-processing', title: 'AI Processing' },
  { id: 'cookies', title: 'Cookies & Analytics' },
  { id: 'third-parties', title: 'Third-Party Providers' },
  { id: 'storage-security', title: 'Storage & Security' },
  { id: 'user-rights', title: 'Your Rights' },
  { id: 'deletion', title: 'Deletion Requests' },
  { id: 'retention', title: 'Data Retention' },
  { id: 'childrens', title: "Children's Privacy" },
  { id: 'international', title: 'International Processing' },
  { id: 'updates', title: 'Policy Updates' },
];

export default function PrivacyPolicyClient() {
  return (
    <PolicyLayout
      title="Privacy Policy"
      description="JobCompanion is committed to protecting your personal information. This policy explains what data we collect, how we use it, and the rights you have over it."
      lastUpdated="May 18, 2026"
      icon={Shield}
      iconBg="bg-primary/10"
      iconColor="text-primary"
      sections={sections}
      currentPath="/privacy-policy"
    >
      <PolicySection id="overview" title="Overview">
        <p>
          JobCompanion (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) operates an AI-assisted job search and application platform currently in controlled beta. This Privacy Policy describes how we collect, use, store, and protect information when you use our services at{' '}
          <a href="https://jobcompanion.app" className="text-primary underline">jobcompanion.app</a>.
        </p>
        <p>
          By using JobCompanion, you agree to the collection and use of information in accordance with this policy. If you do not agree, please discontinue use of the platform.
        </p>
        <PolicyCallout type="info">
          JobCompanion is in controlled beta. Some features described in this policy may not yet be active. We will update this policy as new features are released.
        </PolicyCallout>
      </PolicySection>

      <PolicySection id="data-collected" title="Data We Collect">
        <PolicySubSection title="Account Information">
          <p>When you register, we collect:</p>
          <PolicyList items={[
            'Email address (required for authentication)',
            'Full name (optional, used for personalization)',
            'Password (stored as a secure hash — never in plain text)',
            'Account creation timestamp and last login date',
          ]} />
        </PolicySubSection>
        <PolicySubSection title="Profile & Preferences">
          <PolicyList items={[
            'Job preferences: desired roles, industries, locations, salary range',
            'Work authorization status (if provided)',
            'Remote/hybrid/on-site preferences',
            'Blocked companies list',
            'Auto-apply settings and thresholds (when feature is active)',
          ]} />
        </PolicySubSection>
        <PolicySubSection title="Usage Data">
          <PolicyList items={[
            'Pages visited and features used within the platform',
            'Job searches performed and filters applied',
            'Jobs saved, dismissed, or applied to',
            'AI workflows created and their approval status',
            'Activity log events (resume uploads, analyses, applications)',
          ]} />
        </PolicySubSection>
        <PolicySubSection title="Technical Data">
          <PolicyList items={[
            'IP address (used for security and fraud prevention)',
            'Browser type and version',
            'Device type (desktop, mobile, tablet)',
            'Session identifiers',
            'Error logs and crash reports (anonymized)',
          ]} />
        </PolicySubSection>
      </PolicySection>

      <PolicySection id="resume-uploads" title="Resume Uploads">
        <p>
          When you upload a resume, we store the file securely in our cloud storage (Supabase Storage). Your resume is used exclusively to:
        </p>
        <PolicyList items={[
          'Extract skills, experience, education, and role history for AI analysis',
          'Generate job match scores against available job listings',
          'Pre-fill application drafts when you initiate an AI-assisted workflow',
          'Display your resume history within your account',
        ]} />
        <PolicyCallout type="warning">
          We do not sell, share, or transmit your resume to any employer, recruiter, or third-party data broker. Your resume is processed solely within our platform for your benefit.
        </PolicyCallout>
        <p>
          Resume files are retained until you delete them or request account deletion. See the <a href="#deletion" className="text-primary underline">Deletion Requests</a> section for details.
        </p>
      </PolicySection>

      <PolicySection id="ai-processing" title="AI Processing Disclosure">
        <p>
          JobCompanion uses OpenAI&apos;s API to power resume analysis, job matching, cover letter generation, and career coaching features. When you trigger an AI feature:
        </p>
        <PolicyList items={[
          'Relevant portions of your resume and job description are sent to OpenAI\'s API for processing',
          'OpenAI processes this data under their API data usage policies (not used for model training by default)',
          'AI outputs are returned to our servers and stored in your account',
          'No raw resume files are sent to OpenAI — only extracted text and structured data',
        ]} />
        <PolicyCallout type="info">
          AI-generated content (cover letters, answers, analysis) is stored in your account and is visible only to you. We do not share AI outputs with third parties.
        </PolicyCallout>
      </PolicySection>

      <PolicySection id="cookies" title="Cookies & Analytics">
        <PolicySubSection title="Essential Cookies">
          <p>We use authentication cookies to maintain your login session. These are strictly necessary and cannot be disabled without breaking core functionality.</p>
        </PolicySubSection>
        <PolicySubSection title="Analytics">
          <p>
            If you have not opted out, we may use Google Analytics 4 (GA4) to collect anonymized usage data including page views, session duration, and feature engagement. GA4 data is aggregated and does not personally identify you.
          </p>
          <PolicyList items={[
            'GA4 data is processed by Google under their privacy policy',
            'You can opt out via browser settings or Google\'s opt-out tools',
            'We do not use advertising cookies or behavioral tracking',
          ]} />
        </PolicySubSection>
        <p>See our full <a href="/cookie-policy" className="text-primary underline">Cookie Policy</a> for details.</p>
      </PolicySection>

      <PolicySection id="third-parties" title="Third-Party Providers">
        <p>We use the following third-party services to operate the platform:</p>
        <PolicyList items={[
          'Supabase — database, authentication, and file storage (EU/US data centers)',
          'OpenAI — AI processing for resume analysis and content generation',
          'Google Analytics — anonymized usage analytics (optional)',
          'Adzuna / Jooble — job listing data (when API keys are configured)',
          'Vercel / hosting provider — application hosting and CDN',
        ]} />
        <p>
          Each provider operates under their own privacy policy. We select providers that offer appropriate data protection standards. We do not sell your data to any third party.
        </p>
      </PolicySection>

      <PolicySection id="storage-security" title="Storage & Security">
        <PolicyList items={[
          'All data is stored in Supabase with row-level security (RLS) policies ensuring users can only access their own data',
          'Passwords are hashed using bcrypt — never stored in plain text',
          'Resume files are stored in private Supabase Storage buckets with access controls',
          'All data transmission uses HTTPS/TLS encryption',
          'API keys and secrets are stored as environment variables, never in source code',
          'We conduct periodic security reviews as part of our beta operations',
        ]} />
        <PolicyCallout type="warning">
          No system is 100% secure. While we implement industry-standard protections, we cannot guarantee absolute security. Please use a strong, unique password for your account.
        </PolicyCallout>
      </PolicySection>

      <PolicySection id="user-rights" title="Your Rights">
        <p>Depending on your jurisdiction, you may have the following rights regarding your personal data:</p>
        <PolicyList items={[
          'Right to Access — request a copy of the personal data we hold about you',
          'Right to Rectification — correct inaccurate or incomplete data',
          'Right to Erasure — request deletion of your account and associated data',
          'Right to Portability — receive your data in a machine-readable format',
          'Right to Restriction — limit how we process your data in certain circumstances',
          'Right to Object — object to processing based on legitimate interests',
          'Right to Withdraw Consent — where processing is based on consent',
        ]} />
        <p>
          To exercise any of these rights, contact us at <a href="mailto:privacy@jobcompanion.app" className="text-primary underline">privacy@jobcompanion.app</a>. We will respond within 30 days.
        </p>
      </PolicySection>

      <PolicySection id="deletion" title="Deletion Requests">
        <p>You can request deletion of your account and data at any time by:</p>
        <PolicyList items={[
          'Emailing us at privacy@jobcompanion.app with subject "Account Deletion Request"',
          'Using the account deletion option in Settings (when available)',
        ]} />
        <p>Upon a verified deletion request, we will:</p>
        <PolicyList items={[
          'Delete your account credentials and profile data within 30 days',
          'Remove all uploaded resume files from storage',
          'Delete AI-generated content associated with your account',
          'Anonymize or delete activity logs and usage records',
          'Retain only data required by law or for fraud prevention (see Data Retention)',
        ]} />
      </PolicySection>

      <PolicySection id="retention" title="Data Retention">
        <PolicyList items={[
          'Active account data is retained for the duration of your account',
          'Resume files are retained until deleted by you or upon account deletion',
          'Activity logs are retained for 12 months for security and debugging purposes',
          'Anonymized analytics data may be retained indefinitely',
          'Billing and transaction records (when applicable) are retained for 7 years per legal requirements',
          'Safety and audit logs related to automation may be retained for up to 24 months',
        ]} />
        <p>See our full <a href="/data-retention" className="text-primary underline">Data Retention Policy</a> for details.</p>
      </PolicySection>

      <PolicySection id="childrens" title="Children's Privacy">
        <p>
          JobCompanion is intended for users who are 18 years of age or older. We do not knowingly collect personal information from individuals under 18. If you believe a minor has created an account, please contact us at <a href="mailto:privacy@jobcompanion.app" className="text-primary underline">privacy@jobcompanion.app</a> and we will promptly delete the account.
        </p>
      </PolicySection>

      <PolicySection id="international" title="International Data Processing">
        <p>
          JobCompanion is operated from India. If you access our platform from outside this jurisdiction, your data may be transferred to and processed in countries with different data protection laws.
        </p>
        <p>
          We rely on Supabase&apos;s data processing agreements and standard contractual clauses for international data transfers. By using JobCompanion, you consent to this transfer.
        </p>
      </PolicySection>

      <PolicySection id="updates" title="Policy Updates">
        <p>
          We may update this Privacy Policy from time to time. When we make material changes, we will update the &quot;Last updated&quot; date at the top of this page and, where appropriate, notify you via email or an in-app notice.
        </p>
        <p>
          Continued use of JobCompanion after changes are posted constitutes your acceptance of the updated policy.
        </p>
        <p>
          Questions about this policy? Contact us at <a href="mailto:privacy@jobcompanion.app" className="text-primary underline">privacy@jobcompanion.app</a>.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}
